import React, { useEffect } from 'react';
import { Redirect, useRouteMatch } from 'react-router-dom';
import { NavigationBar, NavigationItem } from '.';
import { useDispatch, useSelector } from 'react-redux';
import { actions as dashboardFilterActions } from '../../../slices/filters/dashboardFilters';
import { selectors as accountListSelectors } from '../../../slices/accounts/list';
import { IIdentifier } from '../../../slices/accounts/account';
import { RootState } from '../../../slices/rootReducer';
import { AccessTypes } from '../domain/accountAccessType';
import { getCurrentRenewal } from '../../../slices/accounts/currentRenewal/currentRenewal.thunk';
import { isExternalClient, isPureProfileClient } from '../../../utils/clients';
import { Features, isFeatureEnabled } from '../../../featureToggles/features';

const SETTINGS_ROUTE = 'settings';
const USERS_ROUTE = 'users';
const BILLING_ROUTE = 'billing';
const INVOICES_ROUTE = 'invoices';
const QUESTION_PACKAGES_ROUTE = 'questionPackages';
const API_KEY_ROUTE = 'apiKey';
const SSO_ROUTE = 'sso';

const navigationRoutes = [
  {
    label: 'Settings',
    route: SETTINGS_ROUTE
  },
  {
    label: 'Users',
    route: USERS_ROUTE
  },
  {
    label: 'Billing',
    route: BILLING_ROUTE
  },
  {
    label: 'Invoices',
    route: INVOICES_ROUTE
  },
  {
    label: 'API Key',
    route: API_KEY_ROUTE
  },
  {
    label: 'SSO',
    route: SSO_ROUTE
  },
  {
    label: 'Question Packages',
    route: QUESTION_PACKAGES_ROUTE
  }
];

interface INavigationBarProps {
  displayBillingTab: boolean;
}

export const AccountsNavigationBar: React.FC<INavigationBarProps> = (props: INavigationBarProps) => {
  const match = useRouteMatch<IIdentifier>();
  const { id = '' } = match.params;
  const account = useSelector((state: RootState) => accountListSelectors.getAccountById(state, id));
  const dispatch = useDispatch();
  const accountId = account?.id;
  const hasInvoicedDeal = !isExternalClient()
    ? account?.currentDeal?.invoiced ?? false
    : true;

  useEffect(() => {
    dispatch(dashboardFilterActions.resetFilters());
  }, [dispatch]);

  useEffect(() => {
    if (accountId && !isExternalClient()) {
      dispatch(getCurrentRenewal({ accountId }));
    }
  }, [dispatch, accountId]);

  const url = match.url.split('/');
  url.pop();
  const baseURL = url.join('/');
  const isEnterpriseAccount = account?.accessType === AccessTypes.Enterprise;
  const isOnBillingTab = match.url.includes(BILLING_ROUTE);
  const isTabDisabled = isEnterpriseAccount && account?.currentDeal !== undefined && !hasInvoicedDeal;

  if (!isOnBillingTab && isTabDisabled && !isExternalClient()) {
    return <Redirect to={`/accounts/${id}/${BILLING_ROUTE}`} />;
  }

  const allowedNavigationRoutes = (route: string) => {
    const pureProfileHiddenTabs = [QUESTION_PACKAGES_ROUTE, SSO_ROUTE];
    if (isPureProfileClient() && pureProfileHiddenTabs.includes(route)) {
      return false;
    }

    if (!isFeatureEnabled(Features.QlNetquest) && route === QUESTION_PACKAGES_ROUTE) {
      return false;
    }

    if (isExternalClient()) {
      const netquestHiddenTabs = [BILLING_ROUTE, INVOICES_ROUTE];
      return !netquestHiddenTabs.includes(route);
    }

    const sightxHiddenTabs = [QUESTION_PACKAGES_ROUTE];
    return isFeatureEnabled(Features.QlNetquest) ? !sightxHiddenTabs.includes(route) : true;
  };

  const navItems: NavigationItem[] = navigationRoutes
    .filter(item => allowedNavigationRoutes(item.route))
    .map(item => ({
      label: item.label,
      path: `${baseURL}/${item.route}`,
      key: item.route,
      disabled: item.route !== BILLING_ROUTE && isTabDisabled
    }));

  if (!props.displayBillingTab) {
    navItems.splice(2, 1);
  }

  return (
    <NavigationBar navItems={navItems} />
  );
};
